import React from 'react'
import PropTypes from 'prop-types'
import shallowCompare from 'react-addons-shallow-compare'
import {EditorState, SelectionState} from 'draft-js'
import {RichTextEditor} from 'common/RichTextEditor'
import Immutable, {Map} from 'immutable'

import cx from 'classnames'
import 'draft-js/dist/Draft.css'

import appStyles from '../../App/appStyles.scss'
import styles from './ReportEditor.scss'

import {SideToolbar} from './SideToolbar'
import {MapBlock} from './MapBlock'
import {ClimbBlock} from './ClimbBlock'
import {GalleryBlock} from './GalleryBlock'
import {JSONBlock} from './JSONBlock'
import {MetaBlock} from './MetaBlock'
import {EmbedBlock} from './EmbedBlock'
import {TRouteMapBlock} from './TRouteMapBlock'
import * as blockUtils from 'lib/blockUtils'
import {getConsole} from 'lib/getConsole'
const console = getConsole('rr', 'ReportEditor')

// Editor responsible for modifying report contents (blocks). Uses draft.js.
export class ReportEditor extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      selectedBlockTop: null
    }
  }

  componentDidMount () {
    if (process.env.NODE_ENV !== 'production') {
      window.getEditorState = () => this.props.editorState
      window.getDraftSelection = () => this.props.editorState.getSelection().serialize()
    }
  }

  blockStyle (contentBlock) {
    const type = contentBlock.getType()
    const mappings = {
      'unstyled': appStyles.paragraph,
      'header-two': appStyles.heading
    }
    return mappings[type]
  }

  onAtomicBlockChange = (block, update) => {
    const {editorState} = this.props
    const contentState = editorState.getCurrentContent()
    const newContentState = contentState
      // applying Map() to update prevents immutable from deeply converting values
      .setIn(['blockMap', block.getKey(), 'data'], block.getData().merge(Map(update)))
      .setIn(['selectionAfter', 'hasFocus'], editorState.getSelection().getHasFocus())
    this.props.onChange(EditorState.push(editorState, newContentState, 'change-block-data'))
  }

  blockRenderer = (block) => {
    const mappings = {
      map: MapBlock,
      gallery: GalleryBlock,
      metablock: MetaBlock,
      climb: ClimbBlock,
      embed: EmbedBlock,
      route: TRouteMapBlock,
      separator: () => <hr />
    }
    if (block.getType() === 'atomic') {
      const {editorState} = this.props
      const contentState = editorState.getCurrentContent()
      const selection = editorState.getSelection()
      return {
        component: mappings[block.getData().get('type')] || JSONBlock,
        editable: false, // very important - prevents crash when user enters text in custom component div
        props: {
          getEditorState: this.getEditorState,
          setEditorState: this.props.onChange,
          resetEditorMode: this.resetEditorMode,
          rides: this.props.rides,
          photos: this.props.photos,
          ridePhotos: this.props.ridePhotos,
          reportPhotos: Immutable.fromJS(this.props.report.photos),
          reportId: this.props.report.id,
          metricUnits: this.props.report.metricUnits,
          onChange: (update) => this.onAtomicBlockChange(block, update),
          onSelected: () => this.selectBlock(block.getKey()),
          isSelected: blockUtils.selectedBlocks(contentState, selection).has(block.getKey())
        }
      }
    }
    return null
  }

  selectBlock = (blockKey) => {
    const {editorState} = this.props
    const contentState = editorState.getCurrentContent()
    // TODO future optimization: if block is already selected, return.
    // Issue with multiple atomic blocks selected at once needs to be resolved
    // before this can be implemented though, see comments in GalleryBlock grabFocusIfSelected
    const selection = SelectionState.createEmpty(blockKey).merge({
      hasFocus: true,
      focusOffset: contentState.getBlockForKey(blockKey).getLength()
    })
    const newContentState = contentState.set('selectionAfter', selection)
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'change-block-data' // not strictly accurate, but eh
    )
    console.log('setting selection to ', selection.serialize())
    this.props.onChange(newEditorState)
  }

  // Useful to get the editorState from a block. Not passing the actual editor
  // state as a prop to every custom block means that they don't re-render every
  // time it changes.
  getEditorState = () => {
    return this.props.editorState
  }

  // used to force atomic blocks to update if dependent data changes
  forceEditorUpdate = () => {
    const {editorState} = this.props
    const newEditorState = EditorState.forceSelection(
      editorState,
      editorState.getSelection()
    )
    this.props.onChange(newEditorState)
  }

  resetEditorMode = () => {
    this._editor.exitCurrentMode()
  }

  shouldComponentUpdate (nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState)
  }

  componentDidUpdate (prevProps) {
    // TODO: this is causing double-rendering in cases where a forced update is
    // needed. Move this logic somewhere else, like onChange, to prevent a double
    // render
    const {rides, photos, editorState} = this.props
    if (!prevProps.rides.equals(rides) || !prevProps.photos.equals(photos)) {
      this.forceEditorUpdate()
    }
    // SideToolbar positioning, requires dom access so we can't do it in render
    const selectionStartKey = editorState.getSelection().getStartKey()
    console.log('selectionStartKey', selectionStartKey)
    const selectedBlock = this._container.querySelector(`[data-block='true'][data-offset-key='${selectionStartKey}-0-0']`)
    const hasFocus = editorState.getSelection().getHasFocus()
    const selectedBlockTop = (hasFocus && selectedBlock) ? selectedBlock.offsetTop : null
    if (selectedBlockTop !== this.state.selectedBlockTop) {
      this.setState({selectedBlockTop})
    }
  }

  removeBlock = (blockKey) => {
    const {editorState} = this.props
    const contentState = editorState.getCurrentContent()
    const newContent = blockUtils.removeBlock(contentState, blockKey)
    this.props.onChange(EditorState.push(editorState, newContent, 'remove-range'))
  }

  setEditorRef = (editor) => {
    this._editor = editor
  }

  blurEditor = () => {
    this._editor && this._editor.blur()
  }

  focusEditor = () => {
    const {editorState} = this.props
    const hasFocus = editorState.getSelection().getHasFocus()
    if (this._editor && !hasFocus) {
      // this functionality can cause particularly hard-to-diagnose bugs, so
      // I'm keeping the console.log & console.trace around for now
      console.log('focusEditor')
      window.console.trace()
      this._editor.focus()
    }
  }

  render () {
    console.log('ReportEditor render')
    const {selectedBlockTop} = this.state
    const {editorState, onChange} = this.props
    return (
      <div
        className={cx(styles.ReportEditor, appStyles.base)}
        ref={(node) => { this._container = node }} >
        <SideToolbar
          editorState={editorState}
          onChange={onChange}
          offset={selectedBlockTop}
          visible={selectedBlockTop !== null}
          removeBlock={this.removeBlock}
          restoreFocus={this.focusEditor.bind(this)} />
        <RichTextEditor
          className={styles.Editor}
          blockRendererFn={this.blockRenderer}
          blockStyleFn={this.blockStyle}
          placeholder='How did your ride go?'
          editorState={editorState}
          onChange={onChange}
          ref={this.setEditorRef} />
      </div>
    )
  }
}

ReportEditor.propTypes = {
  onChange: PropTypes.func
}
